import React, { useContext, useMemo } from 'react';
import {
  Button,
  Typography,
  ColumnLayout,
  Column,
  ModalBody,
  Form,
  FieldSet,
} from '@visionplanner/ui-react-material';
import PropTypes from 'prop-types';
import { StyledModalFooter } from '../../styles';
import * as requests from '../partner-onboarding.requests';
import { PartnerContext } from '../partner-context';
import { useAsyncCallback } from '../../../common/custom-hooks/use-async-callback';
import { reviewFormSchema } from './partner-form-validation';
import useInternationalizationConfiguration from '../../../common/custom-hooks/use-internationalization-configuration';
import { region } from '../../../common/enums';

const TERMS_URL = 'https://www.nmbrs.com/nl/terms/reporting';

const TermsAndConditions = () => (
  <>
    Ik ga akkoord met de
    <a target="_blank" rel="noopener noreferrer" href={TERMS_URL}>
      {' '}
      algemene voorwaarden
    </a>
  </>
);

/** Component to display details of
 *  partner information.
 * */
const Review = ({ fieldOptions }) => {
  const {
    activeStep,
    setActiveStep,
    state,
  } = useContext(PartnerContext);
  const {
    administrationSoftwares,
    payrollSoftwares,
    taxationSoftwares,
    sbiOptions,
    countryTypes,
  } = fieldOptions;

  const {
    firmKeyDetails,
    firmAddress,
    employeeInformation,
    otherSoftwaresUsed,
    invoiceInformation,
    partnerId,
    partnerReviewDetails,
    lastModified,
  } = state;

  const { isRegion, configuration } = useInternationalizationConfiguration();
  const regionId = configuration.region;
  const isBERegion = isRegion(region.BE);
  const handleSubmit = useAsyncCallback(async ({ isPartnerTermsAccepted }) => {
    await requests.saveReviewDetails(partnerId, isPartnerTermsAccepted, lastModified);
    setActiveStep(activeStep + 1);
  }, [partnerId]);

  const findOptionAndReturnLabel = (options, value) => options.find((option) => option.value === +value).label;

  const administrationSoftware = useMemo(() => findOptionAndReturnLabel(administrationSoftwares, otherSoftwaresUsed.administrationSoftwareId),
    [administrationSoftwares, otherSoftwaresUsed.administrationSoftwareId]);
  const payrollSoftware = useMemo(() => findOptionAndReturnLabel(payrollSoftwares, otherSoftwaresUsed.payrollSoftwareId),
    [otherSoftwaresUsed.payrollSoftwareId, payrollSoftwares]);
  const taxationSoftware = useMemo(() => findOptionAndReturnLabel(taxationSoftwares, otherSoftwaresUsed.taxationSoftwareId),
    [otherSoftwaresUsed.taxationSoftwareId, taxationSoftwares]);
  const sbiOption = useMemo(() => findOptionAndReturnLabel(sbiOptions, firmKeyDetails.standardCompanyClassification),
    [firmKeyDetails.standardCompanyClassification, sbiOptions]);
  return (
    <div className="flex-column full-height review-step">
      <Form
        initialValues={partnerReviewDetails}
        validateFormSchema={reviewFormSchema}
        onSubmit={handleSubmit}
        isInitiallyValid={reviewFormSchema.isValidSync(partnerReviewDetails)}
        dataTa="review-submit-details"
        className="full-height flex-column"
      >
        {
          ({
            isSubmitting, isValid,
          }) => (
            <>
              <ModalBody>
                <Typography dataTa="partner-review-header" use="h6">Controleren en bevestigen</Typography>
                <ColumnLayout>
                  <Column span={12} className="flex-column">
                    <Typography dataTa="partner-review-firm-key" use="h6">Basis informatie kantoor</Typography>
                    <Typography dataTa="partner-review-firm-key-kvknumber" use="subtitle2" className="mar-t-xs">{`${isBERegion ? 'Ondernemingsnummer' : 'Kvk-nummer'}: ${firmKeyDetails.kvkNumber}`}</Typography>
                    <Typography dataTa="partner-review-firm-key-companyname" use="subtitle2" className="mar-t-xs">{`Bedrijfsnaam: ${firmKeyDetails.companyName}`}</Typography>
                    <Typography dataTa="partner-review-firm-key-sbioption" use="subtitle2" className="mar-t-xs">{`Standaard bedrijfsindeling: ${sbiOption}`}</Typography>
                    <Typography dataTa="partner-review-firm-key-noofemployees" use="subtitle2" className="mar-t-xs">{`Medewerkers: ${firmKeyDetails.noOfEmployees}`}</Typography>
                    <Typography dataTa="partner-review-firm-key-noofclients" use="subtitle2" className="mar-t-xs">{`Klanten: ${firmKeyDetails.noOfClients}`}</Typography>
                  </Column>
                  <Column span={12} className="flex-column">
                    <Typography dataTa="partner-review-firm-address" use="h6">Kantooradres</Typography>
                    <Typography dataTa="partner-review-firm-countrytype" use="subtitle2" className="mar-t-xs">{`Land: ${countryTypes.filter((type) => type.value === regionId)[0].label}`}</Typography>
                    <Typography dataTa="partner-review-firm-address-address" use="subtitle2" className="mar-t-xs">
                      {`Adres:  ${firmAddress.streetName}, ${firmAddress.houseNumber},
                    ${firmAddress.city} ${firmAddress.postalCode}`}
                    </Typography>
                    <Typography dataTa="partner-review-firm-address-emailaddress" use="subtitle2" className="mar-t-xs">{`Kantoor E-mailadres: ${firmAddress.email}`}</Typography>
                    <Typography dataTa="partner-review-firm-address-phonenumber" use="subtitle2" className="mar-t-xs">{`Kantoor telefoonnummer: ${firmAddress.phoneNumber}`}</Typography>
                    <Typography dataTa="partner-review-firm-address-vatnumber" use="subtitle2" className="mar-t-xs">{`Kantoor Btw-nummer: ${firmAddress.vatNumber}`}</Typography>
                  </Column>
                  {employeeInformation.length > 0
                    && (
                      <Column span={12} className="flex-column">
                        <Typography dataTa="partner-review-add-employees" use="h6">Medewerker informatie</Typography>
                        {employeeInformation.map((employee, index) => (
                          <React.Fragment key={employee.email}>
                            <Typography dataTa={`partner-review-add-empoyee-${index}-name`} use="subtitle2" className="mar-t-xs">
                              {`Naam: ${employee.firstName} 
                              ${employee?.middleName}
                              ${employee.lastName}`}
                            </Typography>
                            <Typography dataTa={`partner-review-add-empoyee-${index}-email`} use="subtitle2" className="mar-t-xs">{`E-mail: ${employee.email}`}</Typography>
                          </React.Fragment>
                        ))}
                      </Column>
                    )}
                  <Column span={12} className="flex-column">
                    <Typography dataTa="partner-review-other-softwares" use="h6">Overige software</Typography>
                    <Typography dataTa="partner-review-other-softwares-administrationSoftware" use="subtitle2" className="mar-t-xs">{`Administratie software: ${administrationSoftware}`}</Typography>
                    <Typography dataTa="partner-review-other-softwares-payrollSoftware" use="subtitle2" className="mar-t-xs">{`Salaris software: ${payrollSoftware}`}</Typography>
                    <Typography dataTa="partner-review-other-softwares-taxationSoftware" use="subtitle2" className="mar-t-xs">{`Aangifte software: ${taxationSoftware}`}</Typography>
                  </Column>
                  <Column span={12} className="flex-column">
                    <Typography dataTa="partner-review-invoice-information" use="h6">Factuurinformatie</Typography>
                    <Typography dataTa="partner-review-invoice-information-accountNumber" use="subtitle2" className="mar-t-xs">{`IBAN: ${invoiceInformation.accountNumber}`}</Typography>
                    <Typography dataTa="partner-review-invoice-information-invoiceEmail" use="subtitle2" className="mar-t-xs">{`Factuur E-mailadres: ${invoiceInformation.invoiceEmail}`}</Typography>
                    <Typography dataTa="partner-review-invoice-information-bankName" use="subtitle2" className="mar-t-xs">{`Naam bank: ${invoiceInformation.bankName}`}</Typography>
                    <Typography dataTa="partner-review-invoice-information-bankAccountnumber" use="subtitle2" className="mar-t-xs">{`Ten name van: ${invoiceInformation.bankAccountName}`}</Typography>
                  </Column>
                  <Column span={12} className="flex-column">
                    <Typography dataTa="terms-and-condition-text" use="h6">Algemene voorwaarden</Typography>
                    <FieldSet
                      type="boolean"
                      name="isPartnerTermsAccepted"
                      controlType="checkbox"
                      dataTa="terms-and-conditions"
                      className="terms-and-conditions"
                      placeholder={<TermsAndConditions />}
                    />
                  </Column>
                </ColumnLayout>
              </ModalBody>
              <StyledModalFooter>
                <Button buttonType="secondary" dataTa="go-to-invoice-information" onClick={() => setActiveStep(activeStep - 1)}>Vorige</Button>
                <Button type="submit" dataTa="review-submit" disabled={!isValid || isSubmitting}>Bevestigen</Button>
              </StyledModalFooter>
            </>
          )
        }
      </Form>
    </div>
  );
};

Review.propTypes = {
  fieldOptions: PropTypes.shape({
    /** List of administrationSoftwares */
    administrationSoftwares: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.number.isRequired,
        label: PropTypes.string.isRequired,
      }),
    ),
    /** List of payrollSoftwares */
    payrollSoftwares: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.number.isRequired,
        label: PropTypes.string.isRequired,
      }),
    ),
    /** List of taxationSoftwares */
    taxationSoftwares: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.number.isRequired,
        label: PropTypes.string.isRequired,
      }),
    ),
    /** List of sbi options */
    sbiOptions: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.number.isRequired,
        label: PropTypes.string.isRequired,
      }),
    ),
  }),
};

export default Review;
